/**
 * 認証ヘルパー関数
 *
 * SecureTokenStorageを使用した認証関連のユーティリティ関数
 */

import { refreshAccessToken } from '@src/api/auth';
import { RouterPath } from '@src/constants/router_path';
import { deleteAllStorageValue } from '@src/modules/local_storage_utils';
import store from '@src/store';
import { clearAuth, setAuth } from '@src/store/slices/auth';
import {
  clearAllTokens,
  getAccessToken as getStoredAccessToken,
  getExpiresAt as getStoredExpiresAt,
  getRefreshToken as getStoredRefreshToken,
  isTokenValid as isStoredTokenValid,
  setAccessToken,
  setExpiresAt,
} from './tokenStorage';

/**
 * アクセストークンを取得
 */
export function getAccessToken(): string | null {
  return getStoredAccessToken();
}

/**
 * リフレッシュトークンを取得
 */
export function getRefreshToken(): string | null {
  return getStoredRefreshToken();
}

/**
 * トークンの有効期限を取得
 */
export function getExpiresAt(): string | null {
  return getStoredExpiresAt();
}

/**
 * トークンが有効かどうかチェック
 */
export function isTokenValid(): boolean {
  return isStoredTokenValid();
}

/**
 * すべてのトークンをクリア
 * アクセストークン、リフレッシュトークン、有効期限をすべて削除します
 */
export { clearAllTokens };

/**
 * 認証ヘッダーを生成
 */
function _getAuthHeaders(): Record<string, string> {
  const token = getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * 認証が必要な処理を実行
 * トークンがない場合はAuthenticationErrorをスロー
 */
function requireAuth(): string {
  const token = getAccessToken();
  if (!token) {
    throw new Error('認証が必要です');
  }
  return token;
}

/**
 * 認証付きリクエストのヘッダーを生成
 * @returns 認証ヘッダーを含むヘッダーオブジェクト
 */
function _createAuthHeaders(additionalHeaders?: Record<string, string>): Record<string, string> {
  const token = requireAuth();
  return {
    ...additionalHeaders,
    Authorization: `Bearer ${token}`,
  };
}

/**
 * トークンが有効期限切れ間近（5分以内）かチェック
 */
function isTokenExpiringSoon(): boolean {
  const expiresAt = getStoredExpiresAt();
  if (!expiresAt) {
    return true;
  }

  const expirationTime = new Date(expiresAt).getTime();
  const currentTime = Date.now();
  const fiveMinutes = 5 * 60 * 1000;

  // 有効期限まで5分以内の場合はリフレッシュが必要
  return expirationTime - currentTime < fiveMinutes;
}

/**
 * トークンをリフレッシュ
 * トークンが有効期限切れ間近または無効な場合、自動的にリフレッシュします
 */
export async function ensureValidToken(): Promise<string | null> {
  // トークンが有効でリフレッシュ不要な場合
  if (isStoredTokenValid() && !isTokenExpiringSoon()) {
    return getStoredAccessToken();
  }

  // リフレッシュトークンがない場合
  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) {
    return null;
  }

  try {
    const response = await refreshAccessToken();

    if (response.success && response.data.accessToken) {
      const newAccessToken = response.data.accessToken;
      const expiresAt = new Date(Date.now() + response.data.expiresIn * 1000).toISOString();

      // トークンストレージを更新
      setAccessToken(newAccessToken);
      setExpiresAt(expiresAt);

      // Redux stateを更新
      const state = store.getState();
      if (state.auth.user) {
        store.dispatch(
          setAuth({
            user: state.auth.user,
            accessToken: newAccessToken,
            refreshToken: refreshToken,
            expiresAt: expiresAt,
          })
        );
      }

      return newAccessToken;
    }

    return null;
  } catch (error) {
    console.error('トークンリフレッシュエラー:', error);

    // トークン更新に失敗した場合、アプリケーションを初期化
    // 1. トークンストレージをクリア
    clearAllTokens();

    // 2. Redux認証状態をクリア
    store.dispatch(clearAuth());

    // 3. ローカルストレージをクリア
    deleteAllStorageValue();

    // 4. ログイン画面へリダイレクト
    if (typeof window !== 'undefined') {
      window.location.href = RouterPath.login;
    }

    return null;
  }
}
